import React from "react";
import { Alert, ArrowLeft, Breadcrumbs, Button, Shield01 } from "@opendate-app/setlist";
import Stepper from "../../shared/Stepper";
import DonationPaymentStep from "../../../shared/DonationPaymentStep";
import { formatPrice } from "../../../shared/teamCurrencyFormatting";
import useWalletLabel from "../../../shared/useWalletLabel";
import { useClassRegistrationCheckoutContext } from "../ClassRegistrationCheckoutContext";
import AttendeesStep from "./AttendeesStep";
import ClassSummaryCard from "./ClassSummaryCard";
import useFocusOnChange from "../../ClassesShared/useFocusOnChange";
import ConfirmationStep, { confirmationAnnouncement } from "./ConfirmationStep";
import OrderSummary from "./OrderSummary";
import HoldCountdown from "./HoldCountdown";

const STEP_ORDER = ["class", "attendees", "payment", "confirmation"];
const STEP_LABELS = { class: "Class", attendees: "Attendees", payment: "Payment", confirmation: "Confirmation" };
const STEP_TITLES = { attendees: "Who's attending?", resuming: "Payment", payment: "Payment", confirmation: "Your registration" };

const FREE_STEP_ORDER = STEP_ORDER.filter((key) => key !== "payment");

const stepperSteps = (current, free) => {
  const order = free ? FREE_STEP_ORDER : STEP_ORDER;
  const currentIndex = order.indexOf(current === "resuming" ? "payment" : current);
  return order.map((key, index) => ({
    key,
    label: STEP_LABELS[key],
    state: index < currentIndex ? "complete" : index === currentIndex ? "current" : "upcoming",
  }));
};

const checkoutAnnouncement = (checkout) => {
  if (checkout.working) return "Holding your spots…";
  if (checkout.step === "resuming") return checkout.resumeFailed ? "" : "Loading your order…";
  if (checkout.step === "confirmation") return confirmationAnnouncement(checkout);
  return "";
};

function Breadcrumb({ catalogUrl, classUrl, name }) {
  const items = [catalogUrl && { label: "Classes", href: catalogUrl }, { label: name, href: classUrl }, { label: "Registration" }].filter(Boolean);
  return (
    <div className="pc-checkout-crumbs">
      <Breadcrumbs items={items} divider="chevron" />
    </div>
  );
}

export default function CheckoutBody() {
  const { checkout, classInstance, classCard, classUrl, catalogUrl, currencyCode, stripePublishableKey, stripeConnectedAccountId, paymentCountry, paymentCurrency, holdExpiresAt } = useClassRegistrationCheckoutContext();
  const resumingOrder = checkout.step === "resuming" || checkout.step === "confirmation" || checkout.order;
  const unavailable = !resumingOrder && classInstance.unavailableMessage;
  const claim = Boolean(holdExpiresAt);
  const showRail = !claim && classCard;
  const Title = claim && checkout.step !== "confirmation" ? "h2" : "h1";
  const showSummary = ["attendees", "payment"].includes(checkout.step) && !unavailable && !checkout.capacityLost;
  const titleRef = useFocusOnChange(checkout.step);
  const walletLabel = useWalletLabel(unavailable ? {} : { stripePublishableKey, stripeConnectedAccountId, paymentCountry, paymentCurrency });

  return (
    <div className={`pc-checkout-layout${showRail ? " pc-checkout-layout--rail" : ""}`} data-checkout-step={checkout.step}>
      <div className="sr-only" role="status" data-testid="checkout-status">{checkoutAnnouncement(checkout)}</div>
      <div className="pc-checkout-main">
        {!claim && <Breadcrumb catalogUrl={catalogUrl} classUrl={classUrl} name={classInstance.name} />}
        {claim && checkout.step !== "confirmation" && <div className="mb-3"><HoldCountdown expiresAt={holdExpiresAt} /></div>}
        {claim && classCard && (
          <div className="mb-3">
            <ClassSummaryCard card={classCard} layout="wide">
              {showSummary && <OrderSummary />}
            </ClassSummaryCard>
          </div>
        )}

        {unavailable ? (
          <div>
            {!claim && <h1 className="pc-checkout__title">{classInstance.name}</h1>}
            <Alert color="warning" role="alert" description={classInstance.unavailableMessage} />
            <div className="pc-checkout-actions">
              <Button as="a" variant="secondary" icon={ArrowLeft} href={classUrl}>Back to Class</Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="pc-steps">
              <Stepper variant="progress" label="Registration steps" steps={stepperSteps(checkout.step, checkout.free)} />
            </div>
            {STEP_TITLES[checkout.step] && !checkout.capacityLost && <Title ref={titleRef} tabIndex={-1} className="pc-checkout__title">{STEP_TITLES[checkout.step]}</Title>}
            {checkout.step === "resuming" && !checkout.resumeFailed && <Alert color="brand" description="Loading your order…" />}
            {checkout.step === "resuming" && checkout.resumeFailed && (
              <div>
                <Alert color="warning" role="alert" description="We couldn't load your order. Check your connection and try again." />
                <div className="mt-2"><Button variant="secondary" onClick={checkout.retryResume}>Try Again</Button></div>
              </div>
            )}
            {checkout.step === "attendees" && <AttendeesStep />}
            {checkout.step === "payment" && checkout.order && (
              <div className="pc-panel">
                {checkout.errors.length > 0 && <div className="mb-3"><Alert color="error" role="alert" description={checkout.errors.join(" ")} /></div>}
                {!holdExpiresAt && checkout.order.hold_expires_at && <div className="mb-3"><HoldCountdown expiresAt={checkout.order.hold_expires_at} variant="payment" /></div>}
                {!showRail && !claim && <div className="mb-3"><OrderSummary /></div>}
                <DonationPaymentStep
                  stripePublishableKey={stripePublishableKey}
                  stripeConnectedAccountId={stripeConnectedAccountId}
                  clientSecret={checkout.order.stripe_client_secret}
                  onSuccess={checkout.onPaymentSuccess}
                  renderSubmit={({ disabled, label }) => (
                    <div className="pc-block-action">
                      <Button type="submit" variant="primary" disabled={disabled} data-testid="donate-button">{label}</Button>
                    </div>
                  )}
                  submitLabel={`Pay ${formatPrice(checkout.order.total, currencyCode)}`}
                />
              </div>
            )}
            {checkout.step === "confirmation" && <ConfirmationStep />}
          </div>
        )}
      </div>

      {showRail && (
        <aside className="pc-checkout-rail" aria-label="Order summary">
          <ClassSummaryCard card={classCard}>
            {showSummary && <OrderSummary />}
          </ClassSummaryCard>
          <div className="pc-panel pc-panel--compact">
            <p className="pc-panel__title">
              <span className="pc-text-success" aria-hidden="true"><Shield01 width={16} height={16} /></span>
              Secure checkout
            </p>
            <p className="pc-muted mb-0">Card payments are processed by Stripe.</p>
            {walletLabel && <p className="pc-muted mb-0 mt-1" data-testid="wallet-label">{walletLabel} supported</p>}
          </div>
        </aside>
      )}
    </div>
  );
}
